import { useRef, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { NbSignal } from "../../lib/jsonContracts";
import { StatusChip } from "../../design-system/primitives/StatusChip";

interface Props {
  rows:          NbSignal[];
  selectedIed:   string;
  sclNodes:      unknown[];
  onRowsChange:  (rows: NbSignal[]) => void;
  onInferSignal: (signal: string, insertAt: number) => Promise<void>;
}

const ROW_H = 38;

// Fila de edición inline — visible en su posición exacta
function EditingRow({
  selectedIed, onConfirm, onCancel,
}: { selectedIed: string; onConfirm: (val: string) => void; onCancel: () => void }) {
  const { t } = useTranslation("signals");
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  return (
    <div className="flex items-center gap-2 px-3 border-b-2 border-[var(--color-brand)] bg-[var(--color-brand-soft-10)]"
      style={{ height: ROW_H, minHeight: ROW_H }}>
      <input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter" && draft.trim()) { onConfirm(draft.trim()); }
          if (e.key === "Escape") { onCancel(); }
        }}
        placeholder={t("editing.placeholder")}
        className="w-96 shrink-0 bg-transparent border-none outline-none font-mono text-[11px] text-[var(--color-brand)] placeholder:text-[var(--color-on-surface-variant)] placeholder:text-[10px]"
      />
      <span className="text-[10px] text-[var(--color-on-surface-variant)] italic flex-1 truncate">
        {selectedIed ? t("editing.iedLabel", { ied: selectedIed }) : t("editing.selectIed")}
      </span>
      <button onClick={onCancel}
        className="shrink-0 text-[var(--color-on-surface-variant)] hover:text-[var(--color-status-absent)] text-xs px-1">✕</button>
    </div>
  );
}

export function SignalsTable({ rows, selectedIed, sclNodes, onRowsChange, onInferSignal }: Props) {
  const { t } = useTranslation("signals");
  const hasSCL = sclNodes.length > 0;

  // Posición de inserción activa: null = sin inserción, número = insertar antes de ese índice
  // -1 = insertar al final
  const [insertPos, setInsertPos] = useState<number | null>(null);
  const [hoveredGap, setHoveredGap] = useState<number | null>(null);
  const [isInferring, setIsInferring] = useState(false);

  const handleConfirm = useCallback(async (signal: string) => {
    if (insertPos === null) return;
    setIsInferring(true);
    const pos = insertPos === -1 ? rows.length : insertPos;
    await onInferSignal(signal, pos);
    setInsertPos(null);
    setIsInferring(false);
  }, [insertPos, rows.length, onInferSignal]);

  const handleCancel = useCallback(() => setInsertPos(null), []);

  const deleteRow = useCallback((idx: number) => {
    onRowsChange(rows.filter((_, i) => i !== idx));
  }, [rows, onRowsChange]);

  // Construir lista visual intercalando la fila de edición
  // Cada item es { type: 'row', row, idx } | { type: 'editing' }
  type Item =
    | { type: 'row'; row: NbSignal; idx: number }
    | { type: 'editing' };

  const items: Item[] = [];
  for (let i = 0; i < rows.length; i++) {
    if (insertPos === i) items.push({ type: 'editing' });
    items.push({ type: 'row', row: rows[i], idx: i });
  }
  // Al final
  if (insertPos === -1 || insertPos === rows.length) {
    items.push({ type: 'editing' });
  }

  // Vista vacía
  if (rows.length === 0 && insertPos === null) {
    return (
      <div className="flex h-full flex-col">
        <TableHeader hasSCL={hasSCL} />
        <div className="flex flex-1 items-center justify-center flex-col gap-3">
          <p className="text-sm text-[var(--color-on-surface-variant)]">
            {selectedIed ? t("empty.withIed", { ied: selectedIed }) : t("empty.noSignals")}
          </p>
          <button onClick={() => setInsertPos(0)}
            className="flex items-center gap-2 rounded border border-[var(--color-brand-border-30)] bg-[var(--color-brand-soft-10)] px-4 py-2 text-sm text-[var(--color-brand)] hover:bg-[var(--color-brand-soft-20)]">
            {t("empty.addFirst")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <TableHeader hasSCL={hasSCL} />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {items.map((item, vi) => {
          if (item.type === 'editing') {
            return (
              <EditingRow key={`edit-${vi}`}
                selectedIed={selectedIed}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
              />
            );
          }
          const { row: r, idx } = item;
          const iedColor = r.status === "present" ? "var(--color-status-present)"
                         : r.status === "absent"  ? "var(--color-status-absent)"
                         : "var(--color-on-surface-variant)";
          const isLast = idx === rows.length - 1;
          return (
            <div key={`row-${idx}`} className="relative group">
              {/* Gap superior — mostrar + para insertar ANTES de esta fila */}
              {insertPos === null && (
                <div
                  onMouseEnter={() => setHoveredGap(idx)}
                  onMouseLeave={() => setHoveredGap(null)}
                  className="absolute top-0 left-0 right-0 flex items-center justify-center"
                  style={{ height: 8, marginTop: -4, zIndex: 10 }}>
                  {hoveredGap === idx && (
                    <button
                      onClick={() => setInsertPos(idx)}
                      className="w-5 h-5 flex items-center justify-center rounded-full bg-[var(--color-brand)] text-black text-xs shadow hover:scale-110 transition-transform leading-none"
                      title={t("row.insertHere")}>+</button>
                  )}
                </div>
              )}

              {/* Fila de datos */}
              <div className="flex items-center gap-2 px-3 border-b border-[var(--color-outline-variant)] hover:bg-[var(--color-surface-variant)]"
                style={{ height: ROW_H }}>
                <span className="w-96 shrink-0 truncate font-mono text-[11px] text-[var(--color-brand)]" title={r.signal}>{r.signal}</span>
                <span className="flex-1 truncate font-mono text-[11px] text-[var(--color-on-surface-variant)] cursor-pointer"
                  title={t("row.copyHint", { node: r.mmsNode })}
                  onClick={() => navigator.clipboard?.writeText(r.mmsNode)}>{r.mmsNode}</span>
                <span className="w-32 shrink-0 truncate text-[11px]">{r.iedRef}</span>
                {hasSCL && <span className="w-40 shrink-0 truncate font-mono text-[10px]" style={{ color: iedColor }}>{r.iedName ?? "—"}</span>}
                {hasSCL && <span className="w-44 shrink-0 truncate text-[10px] text-[var(--color-on-surface-variant)]">{r.dataset ?? "—"}</span>}
                {hasSCL && r.status && <span className="w-24 shrink-0"><StatusChip status={r.status === "present" ? "present" : "absent"} /></span>}
                <button onClick={() => deleteRow(idx)}
                  className="ml-auto shrink-0 opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded hover:bg-[var(--color-surface-bright)] text-[var(--color-on-surface-variant)] hover:text-[var(--color-status-absent)] text-xs transition-opacity"
                  title={t("row.delete")}>✕</button>
              </div>

              {/* Gap inferior de la ÚLTIMA fila — permite añadir al final (C3) */}
              {isLast && insertPos === null && (
                <div
                  onMouseEnter={() => setHoveredGap(rows.length)}
                  onMouseLeave={() => setHoveredGap(null)}
                  className="flex items-center justify-center border-b border-dashed border-[var(--color-outline-variant)]"
                  style={{ height: 24 }}>
                  {hoveredGap === rows.length && (
                    <button
                      onClick={() => setInsertPos(-1)}
                      className="w-5 h-5 flex items-center justify-center rounded-full bg-[var(--color-brand)] text-black text-xs shadow hover:scale-110 transition-transform leading-none"
                      title={t("row.addAtEnd")}>+</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {isInferring && (
          <div className="px-3 py-2 text-[11px] text-[var(--color-brand)] animate-pulse">
            {t("inferring")}
          </div>
        )}
      </div>
    </div>
  );
}

function TableHeader({ hasSCL }: { hasSCL: boolean }) {
  const { t } = useTranslation("signals");
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-surface-container-low)] px-3 py-2 pr-8 text-[11px] font-medium uppercase tracking-wide text-[var(--color-on-surface-variant)]">
      <span className="w-96 shrink-0">{t("header.signalName")}</span>
      <span className="flex-1">{t("header.mmsNode")}</span>
      <span className="w-32 shrink-0">{t("header.iedRef")}</span>
      {hasSCL && <span className="w-40 shrink-0">{t("header.iedNameScl")}</span>}
      {hasSCL && <span className="w-44 shrink-0">{t("header.datasetScl")}</span>}
      {hasSCL && <span className="w-24 shrink-0">{t("header.status")}</span>}
    </div>
  );
}
